import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { api } from '../lib/api';
import { useRedirectIfAuthenticated } from '../hooks/useRedirectIfAuthenticated';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useRedirectIfAuthenticated();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.post('/api/v1/auth/forgot-password', { email });
      setSent(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight={700} mb={1} textAlign="center">
            Reset password
          </Typography>
          {sent ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                If an account with that email exists, you will receive a reset link shortly.
              </Alert>
              <Button component={RouterLink} to="/login" fullWidth variant="outlined">
                Back to sign in
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" mb={3} textAlign="center">
                Enter your email and we&apos;ll send you a link to reset your password.
              </Typography>
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  label="Email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                />
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : undefined}
                  sx={{ mb: 2 }}
                >
                  Send reset link
                </Button>
                <Button component={RouterLink} to="/login" fullWidth variant="text">
                  Back to sign in
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
